import { useState, useCallback, useMemo } from 'react';
import { Box, Snackbar, Typography, IconButton } from '@mui/material';
import {
  Check as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Icon } from '@/components/ui/Icon';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';
import { NotificationContext } from '@/contexts/utils';
import { osirisTokens } from '@/theme/tokens';

export type Severity = 'success' | 'error' | 'warning' | 'info';

const SEVERITY_BG: Record<Severity, string> = {
  success: osirisTokens.state.stabilize,
  error: osirisTokens.bg.errorHover,
  warning: '#d5aa00',
  info: '#6e44f5',
};

const SEVERITY_ICONS: Record<Severity, React.ReactNode> = {
  success: <CheckIcon sx={{ fontSize: 24 }} />,
  // design-system x-octagon per Figma snackbar (28289:5706)
  error: <Icon name="x-octagon" size={24} />,
  warning: <WarningIcon sx={{ fontSize: 24 }} />,
  info: <InfoIcon sx={{ fontSize: 24 }} />,
};

const DEFAULT_TITLE: Record<Severity, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
};

// Success confirmations are transient — auto-dismiss quickly. Errors/warnings
// stay up longer so they can be read.
const SEVERITY_AUTO_HIDE: Record<Severity, number> = {
  success: 5000,
  error: 30000,
  warning: 30000,
  info: 5000,
};

type NotificationContent =
  string | string[] | React.ReactNode | { title: React.ReactNode; subtitle?: React.ReactNode };

function splitContent(
  content: NotificationContent,
  severity: Severity,
): { title: React.ReactNode; subtitle: React.ReactNode | null } {
  if (
    content &&
    typeof content === 'object' &&
    !Array.isArray(content) &&
    'title' in (content as { title: unknown })
  ) {
    const c = content as { title: React.ReactNode; subtitle?: React.ReactNode };
    return { title: c.title, subtitle: c.subtitle ?? null };
  }
  if (Array.isArray(content)) {
    const [first, ...rest] = content;
    return {
      title: first ?? DEFAULT_TITLE[severity],
      subtitle: rest.length ? rest.join(' · ') : null,
    };
  }
  if (typeof content === 'string') {
    return { title: content, subtitle: null };
  }
  return { title: content as React.ReactNode, subtitle: null };
}

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<NotificationContent>('');
  const [severity, setSeverity] = useState<Severity>('success');

  const showNotification = useCallback((message: NotificationContent, sev: Severity) => {
    setContent(message);
    setSeverity(sev);
    setOpen(true);
  }, []);

  const showSuccessNotification = useCallback(
    (message: NotificationContent) => showNotification(message, 'success'),
    [showNotification],
  );

  const showErrorNotification = useCallback(
    (err: string | string[] | FetchBaseQueryError | SerializedError) => {
      if (typeof err === 'string' || Array.isArray(err)) {
        showNotification(err, 'error');
        return;
      }
      if ('data' in err) {
        if (typeof err.data === 'string') {
          showNotification(err.data, 'error');
          return;
        }
        if (err.data && typeof (err.data as { message: string }).message === 'string') {
          showNotification((err.data as { message: string }).message, 'error');
          return;
        }
        if (err.data && Array.isArray((err.data as { message: string[] }).message)) {
          showNotification((err.data as { message: string[] }).message, 'error');
          return;
        }
        showNotification('An error occurred', 'error');
      }
    },
    [showNotification],
  );

  const showInfoNotification = useCallback(
    (message: string | string[]) => showNotification(message, 'info'),
    [showNotification],
  );

  const hideNotification = useCallback(() => setOpen(false), []);

  const contextValue = useMemo(
    () => ({
      showNotification,
      showSuccessNotification,
      showErrorNotification,
      showInfoNotification,
      hideNotification,
    }),
    [
      showNotification,
      showSuccessNotification,
      showErrorNotification,
      showInfoNotification,
      hideNotification,
    ],
  );

  const { title, subtitle } = splitContent(content, severity);
  const bg = SEVERITY_BG[severity];
  // auth pages render no sidebar — there the snackbar sits 16px from the
  // viewport edge, elsewhere 16px to the right of the sidebar
  const hasSidebar = typeof document !== 'undefined' && !!document.querySelector('.sidenav');
  const leftOffset = (hasSidebar ? osirisTokens.layout.sidebarWidth : 0) + 16;

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={SEVERITY_AUTO_HIDE[severity]}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // '&&' beats MUI's breakpoint rule (left: 24px at ≥600px); 16px gap
        // to the right of the side nav so the snackbar doesn't cover it
        sx={{
          zIndex: 2100,
          '&&': {
            left: `${leftOffset}px`,
            bottom: 16,
            right: 'auto',
          },
        }}
      >
        <Box
          className={`osiris-snackbar osiris-snackbar-${severity}`}
          role="alert"
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            minHeight: 40,
            minWidth: 340,
            maxWidth: 'min(1100px, calc(100vw - 520px))',
            borderRadius: '2px',
            overflow: 'hidden',
            color: osirisTokens.text.primary,
            // severity color lives on the root so the close button's hover
            // tint composites over it instead of replacing it
            bgcolor: bg,
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.45)',
          }}
        >
          {/* Icon cell */}
          <Box
            className="osiris-snackbar-icon"
            sx={{
              width: 40,
              minHeight: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: bg,
              flexShrink: 0,
              color: osirisTokens.text.primary,
            }}
          >
            {SEVERITY_ICONS[severity]}
          </Box>
          {/* Text cell */}
          <Box
            className="osiris-snackbar-text"
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '2px',
              px: 2,
              py: 1,
              bgcolor: bg,
              borderLeft: `1px solid ${osirisTokens.bg.secondary}`,
            }}
          >
            <Typography
              className="osiris-snackbar-title"
              component="div"
              sx={{
                fontFamily: osirisTokens.fonts.display,
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: osirisTokens.text.primary,
                lineHeight: 1.2,
              }}
            >
              {title || DEFAULT_TITLE[severity]}
            </Typography>
            {subtitle && (
              <Typography
                className="osiris-snackbar-subtitle"
                component="div"
                sx={{
                  fontFamily: osirisTokens.fonts.body,
                  fontSize: 12,
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                  color: osirisTokens.text.secondary,
                  lineHeight: 1.35,
                  wordBreak: 'break-word',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {/* Close */}
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            className="osiris-snackbar-close"
            sx={{
              width: 40,
              alignSelf: 'stretch',
              borderRadius: 0,
              color: osirisTokens.text.primary,
              bgcolor: 'transparent',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.18)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
